import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { marked } from 'marked'

/**
 * <aphp-transcriptions> — microfrontend AP-HP pour afficher et exporter
 * les transcriptions WhisperX des réunions Meet.
 *
 * Phase 1 (ce fichier) : UI seulement, en mode démo
 *   - Saisie manuelle de l'UUID du recording
 *   - Bouton "Transcrire" → call backend proxy (à venir)
 *   - Affichage markdown rendu + segments par speaker
 *   - Bouton "Exporter Markdown" (téléchargement local)
 *   - Bouton "Créer doc dans Docs" (POST proxy, qui appelle l'API Impress)
 *
 * Phase 2 (à venir, autre itération) :
 *   - Listing automatique des recordings de l'user OIDC (besoin du proxy)
 *   - Authentification via cookies session Meet ou OIDC PKCE direct
 *   - Push automatique vers Docs sans intervention manuelle
 *
 * Pourquoi ce MF (vs patch DINUM upstream) :
 *   Le code DINUM Meet appelle /storage-hook 2 fois (PUT mp4 + retry après
 *   transcription). Le 2e callback échoue en 403 (`is_savable=False`).
 *   Patcher upstream = pull request à merger chez DINUM, lent. Construire
 *   un MF AP-HP qui appelle directement WhisperX + Docs API = souverain,
 *   rapide à livrer, indépendant des release cycles DINUM.
 */
@customElement('aphp-transcriptions')
export class AphpTranscriptions extends LitElement {
  /** URL du proxy backend AP-HP (aphp-transcription-api). */
  @property({ type: String, attribute: 'api-base' }) apiBase = ''
  /** URL Docs Impress pour générer l'URL après push. */
  @property({ type: String, attribute: 'docs-base' }) docsBase = ''
  /**
   * URL de l'API backend Meet (Django) — defaut '' = même origine que la page
   * hôte. Le MF consulte `${meetApiBase}/api/v1.0/recordings/` (cookie session
   * envoyé via `credentials:'include'`) pour récupérer les UUIDs des recordings
   * de l'utilisateur courant, strictement filtrés côté Meet par
   * `RecordingAccess` (cf. RecordingViewSet.get_queryset). C'est la source de
   * vérité — le proxy aphp-transcription-api ne liste plus MinIO directement.
   */
  @property({ type: String, attribute: 'meet-api-base' }) meetApiBase = ''
  /** Pré-remplit l'UUID au mount. Lu auto depuis ?uuid= en query string. */
  @property({ type: String }) uuid = ''
  /** Si "1" / "true", lance la transcription auto au mount. ?autostart=1 */
  @property({ type: String }) autostart = ''
  /**
   * Si "1" / "true", récupère automatiquement le recording le plus récent de
   * l'utilisateur courant (top-1 de l'API Meet, donc strictement filtré par
   * RecordingAccess) ET lance la transcription, sans afficher le sélecteur.
   * Pattern pour embed in-page après une visio Meet (page Feedback).
   */
  @property({ type: String }) latest = ''

  @state() private _uuid = ''
  @state() private _busy = false
  @state() private _error: string | null = null
  @state() private _transcript: TranscriptResult | null = null
  @state() private _docUrl: string | null = null
  @state() private _jobStatus: string | null = null

  override connectedCallback() {
    super.connectedCallback()
    // 1. Initialise _uuid depuis l'attribut HTML (uuid="...") s'il est posé.
    if (this.uuid && !this._uuid) this._uuid = this.uuid
    // 2. Lit ?uuid= et ?autostart= en query string du parent doc — utile pour
    //    être ouvert depuis Meet via un lien (window.open(...)).
    let autostart = false
    let latest = this.latest === '1' || this.latest === 'true'
    try {
      const qs = new URLSearchParams(window.location.search)
      const qUuid = qs.get('uuid')
      if (qUuid && !this._uuid) this._uuid = qUuid
      const qAuto = qs.get('autostart')
      autostart =
        qAuto === '1' || qAuto === 'true' || this.autostart === '1'
      const qLatest = qs.get('latest')
      latest = latest || qLatest === '1' || qLatest === 'true'
    } catch {
      /* SSR ou env sans window */
    }
    // 3. Si `latest`, on récupère le premier recording dispo de l'user (Meet
    //    API user-filtered). On attend ensuite que le statut côté Meet soit
    //    terminal (SAVED/NOTIFICATION_SUCCEEDED) — sinon le fichier mp4/ogg
    //    n'est pas encore dans MinIO et le proxy renvoie "key does not exist".
    //    Le polling protège aussi contre la course (raccrochage → /feedback
    //    avant fin de l'upload egress, typique).
    if (latest) {
      void (async () => {
        const found = await this._loadLatestUserRecording()
        if (!found) {
          this._error =
            'Aucun enregistrement disponible pour votre compte. Vérifiez qu\'une visio a bien été enregistrée.'
          return
        }
        this._uuid = found
        const ready = await this._waitForRecordingReady(found)
        if (!ready) {
          // _waitForRecordingReady positionne déjà _error si statut terminal d'échec.
          return
        }
        void this._onTranscribe()
      })()
    } else if (autostart && this._uuid) {
      // Mode standalone : autostart si UUID déjà fourni.
      setTimeout(() => {
        if (this._uuid && !this._transcript && !this._busy) {
          void this._onTranscribe()
        }
      }, 300)
    }
  }

  /**
   * Poll l'API Meet `/api/v1.0/recordings/{uuid}/` jusqu'à ce que le statut
   * indique que l'audio/vidéo est bien dans MinIO :
   *
   *   - `saved` / `notification_succeeded` → ready ✓
   *   - `aborted` / `failed_to_start` / `failed_to_stop` → échec terminal
   *   - autre (`initiated`, `active`, `stopped`) → on continue à poller
   *
   * Polling 2s, timeout dur à 3 min. Si timeout, on tente quand même la
   * transcription (best effort) ; le proxy renverra une 404 propre si le
   * fichier n'est toujours pas là.
   */
  private async _waitForRecordingReady(uuid: string): Promise<boolean> {
    const base = (this.meetApiBase || '').replace(/\/$/, '')
    const url = `${base}/api/v1.0/recordings/${encodeURIComponent(uuid)}/`
    const terminalOk = new Set(['saved', 'notification_succeeded'])
    const terminalFail = new Set(['aborted', 'failed_to_start', 'failed_to_stop'])

    for (let i = 0; i < 90; i++) {
      // i max = 90 × 2s = 3 min. Suffisant pour un upload S3 d'une visio
      // courte ; pour des visios > 30 min on devra ajuster (à parker dans
      // C.X — observabilité).
      try {
        const res = await fetch(url, { credentials: 'include' })
        if (!res.ok) {
          // Si Meet renvoie 404, le recording a peut-être été supprimé
          // entre-temps — on remonte une erreur claire.
          if (res.status === 404) {
            this._error = 'Enregistrement introuvable côté Meet.'
            return false
          }
          // Autre erreur réseau : on retry quelques fois puis on lâche.
          await new Promise((r) => setTimeout(r, 2000))
          continue
        }
        const data = (await res.json()) as { status?: string }
        const status = (data.status ?? '').toLowerCase()
        this._jobStatus = `enregistrement: ${status || '?'}`
        if (terminalOk.has(status)) return true
        if (terminalFail.has(status)) {
          this._error = `L'enregistrement n'a pas pu être finalisé (statut: ${status}).`
          return false
        }
      } catch (err) {
        // Network glitch ; on patiente puis on retry.
        // (l'erreur sera matérialisée si elle persiste au-delà du timeout)
        void err
      }
      await new Promise((r) => setTimeout(r, 2000))
    }
    // Timeout : on tente quand même la transcription. Si MinIO n'a toujours
    // pas le fichier, l'erreur de transcribe sera affichée proprement.
    return true
  }

  /** True quand le composant est utilisé en mode embed (Feedback.tsx Meet). */
  private get _embedMode(): boolean {
    return this.latest === '1' || this.latest === 'true'
  }

  /**
   * Récupère l'UUID du recording le plus récent de l'utilisateur courant via
   * l'API Meet (`/api/v1.0/recordings/?ordering=-created_at&page_size=1`). Le
   * backend Meet filtre strictement par `RecordingAccess` — un user ne peut
   * voir que les recordings auxquels il a accès. Pas de fuite cross-user.
   *
   * Renvoie l'UUID ou null si l'utilisateur n'a aucun recording / si le call
   * échoue. Cookie de session envoyé via `credentials:'include'` ; appel
   * typiquement same-origin (`meetApiBase=''`) depuis l'embed sur Meet.
   */
  /**
   * Récupère l'identité de l'utilisateur courant via l'API Meet
   * `GET /api/v1.0/users/me/` (same-origin, cookie session). Sert à indiquer
   * au proxy quel user doit devenir `owner` du doc Impress créé :
   *   - `email` : lookup côté Docs (accès role=owner)
   *   - `sub`   : subject OIDC (exposé par le fork Meet ≥ v1.15.0-aphp-16) —
   *     permet au proxy de passer par `create-for-owner` (find-or-create par
   *     sub, même identité que le chemin auto meet-summary).
   * Renvoie {null,null} si l'appel échoue — l'UX dégrade en
   * restricted+editor link.
   */
  private async _fetchMe(): Promise<{ email: string | null; sub: string | null }> {
    try {
      const base = (this.meetApiBase || '').replace(/\/$/, '')
      const res = await fetch(`${base}/api/v1.0/users/me/`, {
        credentials: 'include',
      })
      if (!res.ok) return { email: null, sub: null }
      const data = (await res.json()) as { email?: string; sub?: string }
      return { email: data.email ?? null, sub: data.sub ?? null }
    } catch {
      return { email: null, sub: null }
    }
  }

  private async _loadLatestUserRecording(): Promise<string | null> {
    try {
      const base = (this.meetApiBase || '').replace(/\/$/, '')
      const url = `${base}/api/v1.0/recordings/?ordering=-created_at&page_size=1`
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) {
        this._error = `Impossible de récupérer vos enregistrements (HTTP ${res.status}).`
        return null
      }
      const data = (await res.json()) as { results?: Array<{ id: string }> }
      const id = data.results?.[0]?.id
      return id ?? null
    } catch (err) {
      this._error = err instanceof Error ? err.message : String(err)
      return null
    }
  }

  static styles = css`
    :host {
      display: block;
      font-family: 'Marianne', system-ui, sans-serif;
      max-width: 760px;
      margin: 1rem auto;
      padding: 1.5rem;
      color: #1e1e1e;
    }
    h2 {
      color: #000091;
      margin: 0 0 1rem;
    }
    .form {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }
    input[type='text'] {
      flex: 1;
      padding: 0.5rem 0.75rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-family: ui-monospace, Menlo, monospace;
      font-size: 0.9rem;
    }
    input[type='text']:focus {
      outline: 2px solid #1212ff;
      border-color: #000091;
    }
    button {
      padding: 0.5rem 1.25rem;
      background: #000091;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }
    button:hover:not(:disabled) {
      background: #1212ff;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    button.secondary {
      background: #fff;
      color: #000091;
      border: 1px solid #000091;
    }
    button.secondary:hover:not(:disabled) {
      background: #e3e3fd;
    }
    .error {
      background: #fff4f4;
      color: #ce0500;
      padding: 0.75rem;
      border-radius: 4px;
      margin-bottom: 1rem;
    }
    .meta {
      display: flex;
      gap: 1.5rem;
      flex-wrap: wrap;
      background: #f6f6f6;
      padding: 0.75rem 1rem;
      border-radius: 4px;
      margin-bottom: 1rem;
      font-size: 0.9rem;
    }
    .meta dt {
      font-weight: 600;
      color: #555;
    }
    .meta dd {
      margin: 0 1rem 0 0.25rem;
      display: inline;
      font-family: ui-monospace, Menlo, monospace;
    }
    .actions {
      display: flex;
      gap: 0.5rem;
      margin: 1rem 0;
    }
    .doc-link {
      background: #18753c10;
      border: 1px solid #18753c;
      color: #18753c;
      padding: 0.75rem 1rem;
      border-radius: 4px;
      margin-top: 0.75rem;
    }
    .doc-link a {
      color: #18753c;
      font-weight: 600;
    }
    .transcript {
      background: #fff;
      padding: 1.25rem 1.5rem;
      border: 1px solid #e3e3e3;
      border-radius: 4px;
      line-height: 1.6;
    }
    .transcript h1,
    .transcript h2,
    .transcript h3 {
      color: #000091;
    }
    .segment {
      margin: 0.5rem 0;
      padding: 0.4rem 0.75rem;
      border-left: 3px solid #000091;
      background: #f6f6f6;
    }
    .segment .ts {
      font-family: ui-monospace, Menlo, monospace;
      color: #666;
      font-size: 0.8rem;
      margin-right: 0.5rem;
    }
    .segment .spk {
      font-weight: 600;
      color: #1212ff;
      margin-right: 0.5rem;
    }
    details.rec-list {
      background: #f6f6f6;
      padding: 0.5rem 0.75rem;
      border-radius: 4px;
      margin-bottom: 1rem;
      font-size: 0.9rem;
    }
    details.rec-list summary {
      cursor: pointer;
      font-weight: 600;
      color: #000091;
    }
    details.rec-list ul {
      list-style: none;
      padding: 0;
      margin: 0.5rem 0 0;
    }
    details.rec-list li {
      margin: 0.25rem 0;
    }
    .rec-pick {
      width: 100%;
      text-align: left;
      background: #fff;
      color: #1e1e1e;
      border: 1px solid #ddd;
      padding: 0.4rem 0.6rem;
      border-radius: 3px;
      font-family: inherit;
      cursor: pointer;
      font-size: 0.85rem;
    }
    .rec-pick:hover {
      background: #e3e3fd;
      border-color: #000091;
    }
    .rec-pick code {
      font-family: ui-monospace, Menlo, monospace;
      color: #1212ff;
    }
  `

  private async _onTranscribe() {
    if (!this._uuid.trim()) {
      this._error = 'UUID du recording requis.'
      return
    }
    if (!this.apiBase) {
      this._error =
        'api-base non configuré sur <aphp-transcriptions>. Définis l\'URL du proxy backend.'
      return
    }
    this._busy = true
    this._error = null
    this._transcript = null
    this._docUrl = null
    try {
      const base = this.apiBase.replace(/\/$/, '')
      // Pattern async polling — POST démarre le job (202), GET poll le statut
      // toutes les 2s. Permet de bypasser le timeout LB OVH 50s sur les
      // requêtes longues (WhisperX prend 60-180s).
      const startUrl = `${base}/api/transcriptions/${encodeURIComponent(this._uuid.trim())}/jobs`
      const startRes = await fetch(startUrl, {
        method: 'POST',
        credentials: 'include',
      })
      if (!startRes.ok) {
        throw new Error(`POST ${startUrl} → HTTP ${startRes.status}`)
      }
      const { jobId } = (await startRes.json()) as { jobId: string }
      this._jobStatus = 'pending'

      // Polling avec backoff léger : 1s puis 2s puis stable.
      for (let i = 0; i < 600; i++) {
        const delay = i < 3 ? 1000 : 2000
        await new Promise((r) => setTimeout(r, delay))
        const pollUrl = `${base}/api/transcriptions/jobs/${jobId}`
        const pollRes = await fetch(pollUrl, { credentials: 'include' })
        if (!pollRes.ok) {
          throw new Error(`Polling ${pollUrl} → HTTP ${pollRes.status}`)
        }
        const job = (await pollRes.json()) as {
          status: string
          result?: TranscriptResult
          error?: string
        }
        this._jobStatus = job.status
        if (job.status === 'done') {
          this._transcript = job.result ?? null
          return
        }
        if (job.status === 'error') {
          throw new Error(job.error || 'Transcription failed (unknown error)')
        }
      }
      throw new Error('Transcription timeout (10 min)')
    } catch (err: unknown) {
      this._error = err instanceof Error ? err.message : String(err)
    } finally {
      this._busy = false
      this._jobStatus = null
    }
  }

  private async _onPushToDocs() {
    if (!this._uuid || !this._transcript) return
    if (!this.apiBase) {
      this._error = 'api-base requis pour push to Docs.'
      return
    }
    this._busy = true
    this._error = null
    try {
      // 1) Identifier l'utilisateur connecté à Meet (email + sub OIDC) pour
      //    que le proxy crée le doc à son nom via create-for-owner (sinon le
      //    doc est créé par le service-account et "Supprimer" reste grisé
      //    côté UI Docs). Best-effort : si l'appel échoue, on continue sans
      //    owner — le doc sera créé en restricted+editor link.
      const { email: ownerEmail, sub: ownerSub } = await this._fetchMe()

      // 2) Pattern async polling identique à _onTranscribe — push-to-docs côté
      //    backend retourne 202 + jobId. Le worker côté serveur réutilise le
      //    transcribe en cache si dispo (skip re-whisper). Polling 1-2s.
      //    Évite le timeout LB OVH Octavia 50s sur le POST direct.
      const base = this.apiBase.replace(/\/$/, '')
      const startUrl = `${base}/api/transcriptions/${encodeURIComponent(this._uuid)}/push-to-docs`
      const startRes = await fetch(startUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...(ownerEmail ? { owner_email: ownerEmail } : {}),
          ...(ownerSub ? { owner_sub: ownerSub } : {}),
        }),
      })
      if (!startRes.ok) {
        throw new Error(`POST ${startUrl} → HTTP ${startRes.status}`)
      }
      const { jobId } = (await startRes.json()) as { jobId: string }
      this._jobStatus = 'pending'

      for (let i = 0; i < 600; i++) {
        const delay = i < 3 ? 1000 : 2000
        await new Promise((r) => setTimeout(r, delay))
        const pollUrl = `${base}/api/transcriptions/jobs/${jobId}`
        const pollRes = await fetch(pollUrl, { credentials: 'include' })
        if (!pollRes.ok) {
          throw new Error(`Polling ${pollUrl} → HTTP ${pollRes.status}`)
        }
        const job = (await pollRes.json()) as {
          status: string
          result?: { docId?: string; docUrl?: string }
          error?: string
        }
        this._jobStatus = job.status
        if (job.status === 'done') {
          const r = job.result
          this._docUrl =
            r?.docUrl ??
            (r?.docId
              ? `${this.docsBase.replace(/\/$/, '')}/docs/${r.docId}/`
              : null)
          return
        }
        if (job.status === 'error') {
          throw new Error(job.error || 'push-to-docs failed (unknown error)')
        }
      }
      throw new Error('push-to-docs timeout (10 min)')
    } catch (err: unknown) {
      this._error = err instanceof Error ? err.message : String(err)
    } finally {
      this._busy = false
      this._jobStatus = null
    }
  }

  private _downloadMarkdown() {
    if (!this._transcript) return
    const md = renderMarkdown(this._uuid, this._transcript)
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transcription-${this._uuid}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  override render() {
    // Mode embed (latest=1) : pas de titre, pas d'input, pas de liste.
    // Spinner uniquement tant qu'on n'a pas encore de transcript (busy initial).
    // Une fois la transcription affichée, on garde _renderResult visible —
    // le bouton "Créer dans Docs" gère son propre état busy/_jobStatus
    // pendant le polling push-to-docs.
    if (this._embedMode) {
      return html`
        <h2>Transcription de la réunion</h2>
        ${!this._transcript && !this._error
          ? html`<p style="color: #555">
              ⏳ Récupération du dernier enregistrement et transcription en
              cours…
              ${this._jobStatus
                ? html`<br /><small>statut : ${this._jobStatus}</small>`
                : nothing}
            </p>`
          : nothing}
        ${this._error ? html`<div class="error">${this._error}</div>` : nothing}
        ${this._transcript ? this._renderResult() : nothing}
      `
    }

    return html`
      <h2>Transcriptions Meet AP-HP</h2>

      <p style="color: #555; font-size: 0.9rem; margin-bottom: 1rem;">
        POC microfrontend AP-HP pour récupérer et exporter les transcriptions
        des réunions Meet (workaround souverain du bug DINUM
        <code>/storage-hook</code> 403).
      </p>

      <div class="form">
        <input
          type="text"
          placeholder="UUID du recording (ex: c1f30d41-b21f-4cd5-bcdf-4f54add221c5)"
          .value=${this._uuid}
          @input=${(e: Event) => (this._uuid = (e.target as HTMLInputElement).value)}
          ?disabled=${this._busy}
        />
        <button @click=${this._onTranscribe} ?disabled=${this._busy || !this._uuid.trim()}>
          ${this._busy
            ? `Transcription… ${this._jobStatus ? `(${this._jobStatus})` : ''}`
            : 'Transcrire'}
        </button>
      </div>

      ${this._error ? html`<div class="error">${this._error}</div>` : nothing}
      ${this._transcript ? this._renderResult() : nothing}
    `
  }

  private _renderResult() {
    const data = this._transcript!
    return html`
      <dl class="meta">
        <dt>Recording :</dt>
        <dd>${this._uuid}</dd>
        <dt>Durée :</dt>
        <dd>${formatSeconds(data.duration ?? 0)}</dd>
        <dt>Langue :</dt>
        <dd>${data.language ?? '?'}</dd>
      </dl>

      <div class="actions">
        <button class="secondary" @click=${this._downloadMarkdown}>
          ⬇ Exporter en Markdown
        </button>
        <button @click=${this._onPushToDocs} ?disabled=${this._busy}>
          ${this._busy ? 'Push en cours…' : '↗ Créer dans Docs'}
        </button>
      </div>

      ${this._docUrl
        ? html`<div class="doc-link">
            ✓ Document créé :
            <a href=${this._docUrl} target="_blank" rel="noopener">${this._docUrl}</a>
          </div>`
        : nothing}

      <h3 style="color: #000091">Texte intégral</h3>
      <div class="transcript">
        ${data.text?.trim() ? data.text : html`<em>(vide)</em>`}
      </div>

      ${data.segments?.length
        ? html`
            <h3 style="color: #000091; margin-top: 1.5rem">
              Segments par speaker
            </h3>
            ${data.segments.map(
              (s) => html`
                <div class="segment">
                  <span class="ts">[${formatSeconds(s.start)} → ${formatSeconds(s.end)}]</span>
                  <span class="spk">${s.speaker ?? '?'}</span>
                  <span>${s.text}</span>
                </div>
              `,
            )}
          `
        : nothing}
    `
  }
}

// --- Helpers locaux ---

type Segment = {
  start: number
  end: number
  text: string
  speaker?: string
}

type TranscriptResult = {
  text?: string
  segments?: Segment[]
  duration?: number
  language?: string
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.round(s % 60)
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

function renderMarkdown(uuid: string, data: TranscriptResult): string {
  const lines = [
    '# Transcription Meet',
    '',
    `- **Recording** : \`${uuid}\``,
    `- **Durée** : ${formatSeconds(data.duration ?? 0)}`,
    `- **Langue** : ${data.language ?? '?'}`,
    '',
    '---',
    '',
    '## Texte intégral',
    '',
    data.text?.trim() ?? '_(vide)_',
    '',
    '---',
    '',
    '## Segments par speaker',
    '',
  ]
  let currentSpk: string | undefined
  for (const s of data.segments ?? []) {
    if (s.speaker !== currentSpk) {
      lines.push('', `### ${s.speaker ?? '?'}`, '')
      currentSpk = s.speaker
    }
    lines.push(`**[${formatSeconds(s.start)} → ${formatSeconds(s.end)}]** ${s.text.trim()}`)
  }
  return lines.join('\n') + '\n'
}

// Touch marked import so it survives tree-shaking (used in phase 2 to render
// the markdown preview inline rather than the segment-by-segment view).
void marked

declare global {
  interface HTMLElementTagNameMap {
    'aphp-transcriptions': AphpTranscriptions
  }
}
